'use client'
import * as Sentry from '@sentry/react'
import AudioContainer, { AudioEngine, GlobalAudioPlayer } from '@tunasong/audio-ui'
import { createRTKReduxGraph } from '@tunasong/graph-lib'
import { logger, shortUuid } from '@tunasong/models'
import { useRegisterWebPush } from '@tunasong/notifications'
import type { ExecuteAIPrompt } from '@tunasong/plugin-lib'
import { GlobalAppContext } from '@tunasong/plugin-lib'
import { ConfigService, nlpApi, useStore, useThunkDispatch } from '@tunasong/redux'
import { EntityActionsMenu, ErrorGeneral, ReleaseNotesDialog } from '@tunasong/tunasong-ui'
import { type UIExtensions } from '@tunasong/ui-lib'
import {
  NotificationAlert,
  TunaTheme,
  UIExtensionsContext,
  UploadContext,
  UploadManager,
  useDeviceInfo,
  useFormDialog,
  useGlobalFormDialog,
  useNavigateToEntity,
  useTunaColorScheme,
  useWakeLock,
} from '@tunasong/ui-lib'
import { VendorServices } from '@tunasong/vendor'
import { WebSocketContext, useReceiveRedux } from '@tunasong/ws'
import React, { use, useEffect, useMemo, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import type { $ZodType } from 'zod/v4/core'
import TunaGlobalStyles from './app-global-styles.js'
import { AuthGuard } from './auth/index.js'
import { DIContainer } from './di/di-container.js'
import { FullPageLayout } from './di/layouts/full-page.js'
import { Repository } from './di/plugin-repository.js'
import { useGlobalHotkeys } from './hooks/global-hotkeys.js'
import { useSWSync } from './sw/sw-sync.js'
import { useCreateWebsocket } from './ws/create-websocket.hook.js'
import { DynamicCSP } from './dynamic-csp.js'
import { SEO } from './components/seo.js'

export interface AppProps {
  children?: React.ReactNode
  component: React.FunctionComponent<Record<string, unknown>>
  /** The route requires an authenticated user*/
  auth?: boolean
  /** The route requires the Audio mixer set up. @default false */
  audio?: boolean
  /** Use the specified theme. @default is to use the user's theme */
  theme?: 'light' | 'dark'
}

const configService = new ConfigService()
const audioEngine = new AudioEngine({ configService })

const pluginRepository = new Repository()
const allPlugins = pluginRepository.loadCollection('all')

export const AppContainer: React.FC<AppProps> = ({
  auth = true,
  component: Component,
  theme,
  children,
  ...restProps
}) => {
  /** Global styles */

  const appContainerId = useRef(shortUuid())

  useEffect(() => {
    const containerId = appContainerId.current
    logger.debug(`AppContainer ${containerId} mounted`)
    return () => {
      logger.debug(`AppContainer ${containerId} unmounted`)
    }
  }, [])

  /** Register meta keys.  You can use this by adding data-meta-pressed to the body */
  useEffect(() => {
    const handlePressed = (e: KeyboardEvent) => {
      if (e.metaKey || e.altKey) {
        document.body.setAttribute('data-meta-pressed', 'true')
      }
    }
    const handleDepressed = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.altKey) {
        document.body.removeAttribute('data-meta-pressed')
      }
    }
    document.addEventListener('keydown', handlePressed)
    document.addEventListener('keyup', handleDepressed)
    return () => {
      document.removeEventListener('keydown', handlePressed)
      document.removeEventListener('keyup', handleDepressed)
    }
  }, [])

  const { isIPad, isMobile } = useDeviceInfo()

  /** Receive messages from Service Worker */
  useSWSync()

  const { globalHotkeyModals } = useGlobalHotkeys()

  /** Ensure the mobile page does not lock */
  useWakeLock(isMobile || isIPad)

  /** Request notifications */
  useRegisterWebPush()

  /** Websocket */
  const webSocket = useCreateWebsocket()

  /** Handle redux messages over the websocket */
  useReceiveRedux(webSocket)

  // App specific global App context
  const { getState, subscribe } = useStore()
  const dispatch = useThunkDispatch()
  const navigateToEntity = useNavigateToEntity()
  const prompt = useFormDialog()

  const plugins = use(allPlugins)

  // Upload manager
  const uploadManager = useMemo(
    () =>
      new UploadManager({
        dbName: 'entity-uploads',
        dispatch,
        getState,
        plugins,
      }),
    [dispatch, getState, plugins]
  )

  const AuthContainer = auth ? AuthGuard : React.Fragment

  const { colorScheme: systemScheme } = useTunaColorScheme()
  const colorScheme = theme ?? systemScheme

  const graph = useMemo(() => createRTKReduxGraph({ dispatch }), [dispatch])

  const executeAIPrompt: ExecuteAIPrompt = async ({ messages }) => {
    const result = await dispatch(nlpApi.endpoints.complete.initiate({ prompt: messages })).unwrap()
    return { text: result.text, toolResults: [] }
  }

  const globalAppContext = {
    engine: audioEngine,
    configService,
    /** UIContext extensions for app. These can use components in app, and be surfaced in the standard components. */
    uiExtensions: {
      entityList: {
        ItemMenu: EntityActionsMenu,
      },
    } satisfies UIExtensions,
    getState,
    dispatch,
    subscribe,
    navigateToEntity,
    graph,
    executeAIPrompt,
    prompt: <T extends Record<string, unknown>>({
      schema,
      title,
      defaultValues,
    }: {
      schema: $ZodType<T>
      title: string
      defaultValues?: Record<string, unknown>
    }) =>
      prompt({
        schema,
        defaultValues: defaultValues as never,
        dialogProps: {
          title,
        },
      }),
  }

  const { formDialog } = useGlobalFormDialog()

  const { dndBackend, dndOptions } = useMemo(() => {
    if (isMobile || isIPad) {
      return {
        dndBackend: TouchBackend,
        dndOptions: {
          enableMouseEvents: true,
          delayTouchStart: 200,
        },
      }
    }
    return { dndBackend: HTML5Backend, dndOptions: {} }
  }, [isIPad, isMobile])

  return (
    <Sentry.ErrorBoundary fallback={ErrorGeneral}>
      <GlobalAppContext.Provider value={globalAppContext}>
        <UploadContext.Provider value={uploadManager}>
          <VendorServices>
            <UIExtensionsContext.Provider value={globalAppContext?.uiExtensions ?? null}>
              <TunaTheme colorScheme={colorScheme}>
                <TunaGlobalStyles />
                <SEO title="Tunasong" />
                <DynamicCSP />

                <FullPageLayout>
                  <WebSocketContext.Provider value={webSocket}>
                    <DIContainer pluginRepository={pluginRepository}>
                      <DndProvider backend={dndBackend} options={dndOptions}>
                        <AuthContainer>
                          <ReleaseNotesDialog />
                          <NotificationAlert />
                          <AudioContainer engine={globalAppContext?.engine ?? null}>
                            <GlobalAudioPlayer key={`${appContainerId}-global-player`} />
                            <Component {...restProps} />
                            {/* Prompt / form dialog */}
                            {formDialog}
                            {globalHotkeyModals}
                            {children}
                          </AudioContainer>
                        </AuthContainer>
                      </DndProvider>
                    </DIContainer>
                  </WebSocketContext.Provider>
                </FullPageLayout>
              </TunaTheme>
            </UIExtensionsContext.Provider>
          </VendorServices>
        </UploadContext.Provider>
      </GlobalAppContext.Provider>
    </Sentry.ErrorBoundary>
  )
}

export default AppContainer
